import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddonPacksService from "../../../Api/AddonPacksService";
import VouchersService from "../../../Api/VouchersService";
import PaymentService from "../../../Api/PaymentService";
import ClientFooter from "../ClientFooter";
import { AuthContext } from "../../../Context/AuthContext";
import '../../../styles/common.css';

const AddonPackDetailesView = () => {
    const [pack, setPack] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [voucherCode, setVoucherCode] = useState("");
    const [voucherError, setVoucherError] = useState("");
    const [voucherApplied, setVoucherApplied] = useState(false);
    const [discountedPrice, setDiscountedPrice] = useState(null);
    const [voucherDetails, setVoucherDetails] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const addonPacksService = new AddonPacksService();
    const vouchersService = new VouchersService();
    const paymentService = new PaymentService();
    const { cookies } = useContext(AuthContext);

    // Debug log for component mount
    console.log("AddonPackDetailesView component mounted");

    useEffect(() => {
        // Debug log for useEffect
        console.log("useEffect triggered");
        console.log("Current location:", location);
        
        const params = new URLSearchParams(location.search);
        const packId = params.get('id');
        console.log("Pack ID from URL:", packId);
        
        if (!packId) {
            console.log("No pack ID found in URL");
            setError("No pack ID provided");
            setLoading(false);
            return;
        }

        // Debug log before API call
        console.log("Attempting to fetch pack details for ID:", packId);
        
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                console.log("Making API call to GetAddonPackByIdPublic");
                const response = await addonPacksService.GetAddonPackByIdPublic(packId);
                console.log("API Response received:", response);
                
                if (response?.data?.status === "success") {
                    console.log("Setting pack data:", response.data.data);
                    setPack(response.data.data);
                    setError(null);
                } else {
                    console.error("Invalid response format:", response);
                    setError("Invalid response from server");
                }
            } catch (error) {
                console.error("Error in fetchData:", error);
                if (error.message.includes("Network Error")) {
                    setError("Unable to connect to the server. Please check your internet connection.");
                } else if (error.response) {
                    setError(error.response.data.message || "Failed to fetch pack details");
                } else {
                    setError("An unexpected error occurred");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Load Razorpay script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [location.search]); // Changed dependency to location.search

    const handleVoucherSubmit = async (e) => {
        e.preventDefault();
        setVoucherError("");

        if (!voucherCode.trim()) {
            setVoucherError("Please enter a voucher code");
            return;
        }

        try {
            const response = await vouchersService.validateVoucher({
                code: voucherCode,
                product_type: "addon_pack",
                product_id: pack.id
            });

            if (response.data.status === "success" && response.data.data.valid) {
                setVoucherApplied(true);
                setVoucherDetails(response.data.data.voucher);

                // Calculate discounted price
                const originalPrice = parseFloat(pack.price);
                let newPrice;

                if (response.data.data.voucher.voucher_type === "percentage") {
                    const discount = originalPrice * (parseFloat(response.data.data.voucher.discount_value) / 100);
                    newPrice = originalPrice - discount;
                } else {
                    newPrice = originalPrice - parseFloat(response.data.data.voucher.discount_value);
                }

                setDiscountedPrice(newPrice.toFixed(2));
                setVoucherError("");
            } else {
                setVoucherError("Invalid voucher code");
                setVoucherApplied(false);
                setVoucherDetails(null);
                setDiscountedPrice(null);
            }
        } catch (error) {
            setVoucherError("Error validating voucher");
            setVoucherApplied(false);
            setVoucherDetails(null);
            setDiscountedPrice(null);
        }
    };

    const handlePayment = async () => {
        try {
            if (!pack || !pack.id) {
                console.error("Invalid pack data:", pack);
                alert("Invalid pack information. Please try again.");
                return;
            }

            if (!cookies.token) {
                console.log("No authentication token found");
                navigate('/StudentLogin');
                return;
            }

            console.log("Starting payment process for pack:", pack);
            
            try {
                const paymentData = {
                    pack_id: pack.id,
                    voucher_code: voucherCode || null
                };
                console.log("Payment data:", paymentData);

                const orderResponse = await paymentService.AddAddonPayment(paymentData);
                console.log("Payment order response:", orderResponse);
                
                if (!orderResponse?.data?.status === "success") {
                    throw new Error("Invalid response from payment server");
                }

                const { key_id, amount, order_id } = orderResponse.data.data;
                
                if (!key_id || !amount || !order_id) {
                    throw new Error("Missing required payment data");
                }

                console.log("Razorpay key received:", key_id);
                
                const options = {
                    key: key_id,
                    amount: amount,
                    currency: "INR",
                    name: "Day Trading",
                    description: `Payment for ${pack.pack_name}`,
                    order_id: order_id,
                    handler: async function (response) {
                        try {
                            console.log("Payment successful, verifying...");
                            // Verify payment
                            const verifyData = {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature
                            };
                            console.log("Verification data:", verifyData);

                            const verifyResponse = await paymentService.VerifyPayment(verifyData);
                            console.log("Verification response:", verifyResponse);
                            
                            if (verifyResponse?.data?.status === "success") {
                                alert("Payment successful!");
                                navigate('/StudentDashboard');
                            } else {
                                console.error("Payment verification failed:", verifyResponse);
                                alert("Payment verification failed!");
                            }
                        } catch (error) {
                            console.error("Payment verification error:", error);
                            alert("Payment verification failed! Please contact support.");
                        }
                    },
                    prefill: {
                        name: cookies.user?.name || "User",
                        email: cookies.user?.email || "",
                        contact: cookies.user?.phone || ""
                    },
                    theme: {
                        color: "#3399cc"
                    }
                };

                console.log("Initializing Razorpay with options:", options);
                const razorpay = new window.Razorpay(options);
                razorpay.open();
            } catch (error) {
                console.error("Payment order creation failed:", error);
                if (error.response) {
                    console.error("Error response:", error.response.data);
                    alert(error.response.data.message || "Payment initiation failed! Please try again.");
                } else {
                    alert("Payment initiation failed! Please try again.");
                }
            }
        } catch (error) {
            console.error("Payment error details:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            alert("Payment initiation failed! Please try again.");
        }
    };

    if (loading) {
        return (
            <div className="rbt-section-gap">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h3>Loading...</h3>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rbt-section-gap">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h3 className="text-danger">{error}</h3>
                            <button 
                                className="rbt-btn btn-gradient mt-3"
                                onClick={() => navigate('/')}
                            >
                                Return to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!pack) {
        return (
            <div className="rbt-section-gap">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h3>Pack not found</h3>
                            <button 
                                className="rbt-btn btn-gradient mt-3"
                                onClick={() => navigate('/')}
                            >
                                Return to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="rbt-breadcrumb-default rbt-breadcrumb-style-3">
                <div className="breadcrumb-inner">
                    <img src="assets1/images/bg/bg-image-10.jpg" alt="Education Images" />
                </div>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="content">
                                <div className="content text-start">
                                    <ul className="page-list">
                                        <li className="rbt-breadcrumb-item">
                                            <a href="index.html">Home</a>
                                        </li>
                                        <li>
                                            <div className="icon-right">
                                                <i className="feather-chevron-right" />
                                            </div>
                                        </li>
                                        <li className="rbt-breadcrumb-item active">{pack.pack_name}</li>
                                    </ul>
                                    <h2 className="title">
                                        {pack.pack_name}
                                    </h2>
                                    <p className="description">
                                        {pack.description}
                                    </p>

                                    <ul className="rbt-meta">
                                        <li>
                                            <i className="feather-star" />
                                            Points: {pack.points_provided}
                                        </li>
                                        <li>
                                            <i className="feather-check-circle" />
                                            {pack.is_active ? 'Active' : 'Inactive'}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rbt-course-details-area ptb--60">
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-8">
                            <div className="course-details-content">
                                <div className="rbt-course-feature-box rbt-border-with-box thuumbnail">
                                    <img
                                        className="w-100"
                                        src={pack.thumbnail_url}
                                        alt={pack.pack_name}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="course-sidebar sticky-top rbt-border-with-box course-sidebar-top rbt-gradient-border">
                                <div className="inner">
                                    <div className="content-item-content">
                                        <div className="rbt-price-wrapper d-flex flex-wrap align-items-center justify-content-between">
                                            <div className="rbt-price">
                                                {discountedPrice ? (
                                                    <>
                                                        <span className="current-price">₹{discountedPrice}</span>
                                                        <span className="off-price">₹{pack.price}</span>
                                                    </>
                                                ) : (
                                                    <span className="current-price">₹{pack.price}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="voucher-section mt--15">
                                            <form onSubmit={handleVoucherSubmit} className="d-flex gap-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter voucher code"
                                                    value={voucherCode}
                                                    onChange={(e) => setVoucherCode(e.target.value)}
                                                />
                                                <button
                                                    type="submit"
                                                    className="rbt-btn btn-gradient btn-sm"
                                                >
                                                    Apply
                                                </button>
                                            </form>
                                            {voucherError && (
                                                <div className="text-danger mt-2">{voucherError}</div>
                                            )}
                                            {voucherApplied && voucherDetails && (
                                                <div className="text-success mt-2">
                                                    Voucher applied! {voucherDetails.voucher_type === "percentage" ?
                                                        `${voucherDetails.discount_value}% off` :
                                                        `₹${voucherDetails.discount_value} off`}
                                                </div>
                                            )}
                                        </div>

                                        <div className="add-to-cart-button mt--15">
                                            <a
                                                className="rbt-btn btn-gradient icon-hover w-100 d-block text-center"
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (cookies.token) {
                                                        handlePayment();
                                                    } else {
                                                        navigate('/StudentLogin');
                                                    }
                                                }}
                                            >
                                                <span className="btn-text">{cookies.token ? 'Buy Now' : 'Login to Buy'}</span>
                                                <span className="btn-icon">
                                                    <i className="feather-arrow-right" />
                                                </span>
                                            </a>
                                        </div>

                                        <div className="rbt-widget-details mt--20">
                                            <ul className="rbt-course-details-list-wrapper">
                                                <li>
                                                    <span>Points Provided</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {pack.points_provided}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span>Status</span>
                                                    <span className="rbt-feature-value rbt-badge-5">
                                                        {pack.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rbt-progress-parent">
                <svg
                    className="rbt-back-circle svg-inner"
                    width="100%"
                    height="100%"
                    viewBox="-1 -1 102 102"
                >
                    <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
                </svg>
            </div>

            <ClientFooter />
        </>
    );
};

export default AddonPackDetailesView; 